import cn from 'classnames'
import { CSSProperties, useCallback, useMemo } from 'react'
import { animated, useSpring } from 'react-spring'

import Button from '~elements/Button'
import useCookie from '~hooks/useCookie'
import AstronautIllustration from '~icons/illustrations/astronaut.svg'
import PlanetIllustration from '~icons/illustrations/moon.svg'
import { AUTH_DATA_KEYS } from '~lib/constants'
import ROUTES, { isAuthorized } from '~lib/routes'
import LinkWrapper from '~lib/ui-kit/LinkWrapper'
import styles from '~styles/Error.module.scss'

export interface ErrorPageProps {
  statusCode?: 404 | 500
}

const calc = (x, y) => [x - window.innerWidth / 2, y - window.innerHeight / 2]

const charsTransitions = [
  (x, y) => `translate3d(${x / 13.5}px,${y / 13.5}px,0)`,
  (x, y) => `translate3d(${x / 15}px,${y / 15}px,0)`,
  (x, y) => `translate3d(${x / 13.5}px,${y / 13.5}px,0)`,
]

const astronautTransition = (x, y) => `translate3d(${x / 8}px,${y / 8}px,0)`

const contentByStatusCode = {
  404: {
    heading: `Oops! Looks like you're lost in space`,
    text: `We couldn't find the page you're looking for`,
  },
  500: {
    heading: 'Oops! Something went wrong',
    text: 'We apologise and are fixing the problem. Please try again at a later stage',
  },
}

export default function ErrorPage({ statusCode }) {
  const statusCodeChars = useMemo(() => {
    return statusCode.toString().split('')
  }, [statusCode])

  const [props, set] = useSpring(() => ({ xy: [0, 0], config: { mass: 10, tension: 550, friction: 140 } }))
  const astronautStyles = useMemo(() => ({ transform: props.xy.to(astronautTransition) }), [props.xy])

  const handleMouseMove = useCallback(({ clientX: x, clientY: y }: any) => set({ xy: calc(x, y) }), [set])

  const cookie = useCookie()

  const backHref = useMemo(
    () => (isAuthorized(cookie) ? `/${cookie[AUTH_DATA_KEYS.ORGANIZATION_SLUG]}/launches` : ROUTES.AUTH),
    [cookie]
  )

  return (
    <div className={styles.root} onMouseMove={handleMouseMove}>
      <animated.div className={styles.backgroundContainer}>
        <div className={cn(styles.orbit, styles.orbit1)} />
        <div className={cn(styles.orbit, styles.orbit2)} />
        <div className={cn(styles.orbit, styles.orbit3)} />
      </animated.div>
      <animated.div className={styles.statusCode}>
        {statusCodeChars.map((char, index) => {
          const charStyles = {
            transform: props.xy.interpolate(charsTransitions[index]),
          } as unknown as CSSProperties

          if (Number(char) === 0 && index === 1) {
            return (
              <animated.span key={index} className={styles.planet} style={charStyles}>
                <PlanetIllustration />
              </animated.span>
            )
          }
          return (
            <animated.span key={index} className={styles.char} style={charStyles}>
              {char}
            </animated.span>
          )
        })}
        <animated.span className={styles.astronaut} style={astronautStyles}>
          <AstronautIllustration />
        </animated.span>
      </animated.div>
      <div className={styles.content}>
        <h3 className={styles.heading}>{contentByStatusCode[statusCode]?.heading}</h3>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        <p className={styles.text}>{contentByStatusCode[statusCode]?.text}</p>
        <LinkWrapper href={backHref}>
          <Button variant="lavender" size="xl">
            Back home
          </Button>
        </LinkWrapper>
      </div>
    </div>
  )
}
